import { Track } from "@/src/lib/tracking";
/**
 * App-Web Bridge 核心工具函数
 * 用于 Next.js 项目中与 Android WebView 和 iOS WebView 进行双向通信
 */

import { buildAdjustPayloadFromEventKey } from "./adjustEventConfig";
import {
  getContainerRouteContext,
  NATIVE_DETAIL_CONTAINER_ID,
} from "./containerRouteMeta";
import {
  MessageType,
  ContainerBroadcastEvent,
  ContainerControlAction,
  Platform,
  BaseMessage,
  ResponseMessage,
  type BridgeErrorCode,
  type BridgeErrorPayload,
  type OpenDetailInNativeOptions,
  type NativeStatusBarStyle,
  type AdjustAttributionData,
  type OsGetAdvertisingIdData,
  type OsAppTrackingTransparencyData,
  type AdjustTrackEventPayload,
  type AdjustTrackSubscriptionPayload,
  type AdjustUpdateSkanConversionValuePayload,
  type DeepLinkOpenedData,
} from "./types";

/** request() reject 时附带的结构化错误（若原生返回了 code/errMsg） */
export type BridgeRequestError = Error & { bridgeError?: BridgeErrorPayload };
// default preference bridge messages（Web 侧 wrapper）
// 新协议：仅一个 type，通过 data.action 区分 get/set/delete/clearAll
const BRIDGE_MSG_DEFAULT_PREF = "default-preference" as const;

type BridgeTimeoutDebugBucket = {
  total: number;
  byType: Record<string, number>;
};

const BRIDGE_TIMEOUT_DEBUG_WINDOW_MS = 1000;
const ENABLE_BRIDGE_TIMEOUT_DEBUG = process.env.NEXT_PUBLIC_ENV !== "prod";
let bridgeTimeoutDebugBucket: BridgeTimeoutDebugBucket = {
  total: 0,
  byType: {},
};
let bridgeTimeoutDebugFlushTimer: ReturnType<typeof setTimeout> | null = null;

function recordBridgeTimeout(type: string, timeoutMs: number) {
  if (!ENABLE_BRIDGE_TIMEOUT_DEBUG) {
    return;
  }
  bridgeTimeoutDebugBucket.total += 1;
  bridgeTimeoutDebugBucket.byType[type] =
    (bridgeTimeoutDebugBucket.byType[type] ?? 0) + 1;

  if (bridgeTimeoutDebugFlushTimer != null) {
    return;
  }

  bridgeTimeoutDebugFlushTimer = setTimeout(() => {
    const snapshot = bridgeTimeoutDebugBucket;
    bridgeTimeoutDebugBucket = { total: 0, byType: {} };
    bridgeTimeoutDebugFlushTimer = null;
    const byTypeEntries = Object.entries(snapshot.byType).sort(
      (a, b) => b[1] - a[1],
    );

    Track.log("error", "Bridge", "[timeout][window-summary]", {
      windowMs: BRIDGE_TIMEOUT_DEBUG_WINDOW_MS,
      timeoutMs,
      total: snapshot.total,
      byType: String(snapshot.byType),
      byTypeText: byTypeEntries.map(([k, v]) => `${k}:${v}`).join(", "),
    });
  }, BRIDGE_TIMEOUT_DEBUG_WINDOW_MS);
}

const PAYMENT_TRACE_INCOMING_TYPES = new Set<string>([
  MessageType.PAYMENT_PURCHASE_SUCCESS,
  MessageType.PAYMENT_PURCHASE_RESULT,
  MessageType.SHELL_WEBVIEW_INTERACTIVE,
]);

function logPaymentTraceFromApp(message: {
  type?: string;
  id?: string;
  data?: unknown;
}): void {
  if (!message.type || !PAYMENT_TRACE_INCOMING_TYPES.has(message.type)) {
    return;
  }
  const data =
    message.data && typeof message.data === "object"
      ? (message.data as Record<string, unknown>)
      : undefined;
  Track.log("info", "Bridge", "[payment-trace] received from App", {
    type: message.type,
    messageId: message.id,
    productId: typeof data?.productId === "string" ? data.productId : undefined,
    reason: typeof data?.reason === "string" ? data.reason : undefined,
    success: typeof data?.success === "boolean" ? data.success : undefined,
    tabIndex: typeof data?.tabIndex === "number" ? data.tabIndex : undefined,
  });
}

/**
 * 解析 Bridge 统一错误结构 { code, errMsg }
 */
export function parseBridgeErrorPayload(
  error: unknown,
): BridgeErrorPayload | undefined {
  if (error == null) {
    return undefined;
  }
  if (typeof error === "object" && error !== null) {
    const e = error as { code?: unknown; errMsg?: unknown };
    if (typeof e.code === "string" && typeof e.errMsg === "string") {
      return {
        code: e.code as BridgeErrorCode,
        errMsg: e.errMsg,
      };
    }
  }
  return undefined;
}

export function getNativeErrorMessage(error: unknown): string | undefined {
  if (error == null) {
    return undefined;
  }
  const bridgeErr = parseBridgeErrorPayload(error);
  if (bridgeErr) {
    return bridgeErr.errMsg;
  }
  if (typeof error === "string") {
    const trimmed = error.trim();
    // 兼容原生直接返回 JSON 字符串：{"code":...,"message":...} / {"msg":...} / {"error":...}
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        return getNativeErrorMessage(parsed);
      } catch {
        // ignore
      }
    }
    return error;
  }
  if (error instanceof Error) {
    return getNativeErrorMessage(error.message) ?? error.message;
  }
  if (typeof error === "object") {
    const e = error as {
      message?: unknown;
      msg?: unknown;
      error?: unknown;
      errorMessage?: unknown;
      code?: unknown;
      name?: unknown;
      data?: unknown;
    };
    const directMessage =
      (typeof e.message === "string" && e.message) ||
      (typeof e.msg === "string" && e.msg) ||
      (typeof e.errorMessage === "string" && e.errorMessage) ||
      (typeof e.error === "string" && e.error) ||
      undefined;
    if (directMessage) {
      const code =
        typeof e.code === "number" || typeof e.code === "string"
          ? String(e.code)
          : undefined;
      const name = typeof e.name === "string" ? e.name : undefined;
      const suffix = [name, code].filter(Boolean).join(" ");
      return suffix ? `${directMessage} (${suffix})` : directMessage;
    }
    // 常见嵌套：{ data: { error/message/msg } }
    if (e.data && typeof e.data === "object") {
      const d = e.data as { message?: unknown; msg?: unknown; error?: unknown };
      const nestedMessage =
        (typeof d.message === "string" && d.message) ||
        (typeof d.msg === "string" && d.msg) ||
        (typeof d.error === "string" && d.error) ||
        undefined;
      if (nestedMessage) {
        return nestedMessage;
      }
    }
    try {
      const s = JSON.stringify(error);
      return s && s !== "{}" ? s : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/**
 * 检测当前运行平台
 */
/** 读取 `locale` cookie（切换语言后供原生重写其它 Tab URL 路径前缀） */
function readLocaleCookieForShellSync(): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  try {
    const match = document.cookie.match(/(?:^|;\s*)locale=([^;]*)/);
    if (!match?.[1]) {
      return null;
    }
    const value = decodeURIComponent(match[1].trim());
    return value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

export function detectPlatform(): Platform {
  if (typeof window === "undefined") {
    return "web";
  }

  // 检测 iOS
  if ((window as any).webkit?.messageHandlers?.bridge) {
    return "ios";
  }

  // 检测 Android WebView 模式（通过 JavaScript Interface）
  if (
    (window as any).bridge &&
    typeof (window as any).bridge.handleMessage === "function"
  ) {
    return "android";
  }

  return "web";
}

/**
 * Bridge 类 - 管理 App-Web 通信
 */
export class Bridge {
  private platform: Platform;
  private messageQueue: BaseMessage[] = [];
  private responseHandlers: Map<string, (response: ResponseMessage) => void> =
    new Map();
  private messageListeners: Map<string, Set<(data: any) => void>> = new Map();
  private ready: boolean = false;
  private appInitProcessed: boolean = false; // 标记 app-init 是否已处理

  /**
   * 初始化时机：单例首次 getBridge() 时构造，init() 不在此处直接调用。
   * - 若首次检查时 App 的 bridge 未注入，会每 100ms 轮询 tryInit，直到检测到
   *   __ANDROID_BRIDGE_READY__ / window.bridge / webkit.messageHandlers.bridge 再调用 init()，
   *   因此「先实例化、后建立 App bridge」不会导致注入/监听永久不生效。
   * - 约 30s 内未检测到则按纯 Web 执行 init() 并停止轮询。
   */
  constructor() {
    this.platform = detectPlatform();

    if (typeof window !== "undefined") {
      const MAX_INIT_RETRIES = 300; // 约 30s 后不再轮询，避免纯 Web 环境无限轮询
      let initRetryCount = 0;

      const tryInit = () => {
        try {
          const bridgeReady = (window as any).__ANDROID_BRIDGE_READY__;
          const hasBridge =
            (window as any).bridge &&
            typeof (window as any).bridge.handleMessage === "function";
          const hasWebKit = (window as any).webkit?.messageHandlers?.bridge;

          if (bridgeReady || hasBridge || hasWebKit) {
            this.init();
            return;
          }
          initRetryCount += 1;
          if (initRetryCount < MAX_INIT_RETRIES) {
            setTimeout(tryInit, 100);
          } else {
            // 超时仍未检测到 App bridge，按纯 Web 处理并执行一次 init，停止轮询
            this.init();
          }
        } catch (e) {
          Track.log("warn", "Bridge", "tryInit error, will retry", {
            cause: String(e),
          });
          if (initRetryCount < MAX_INIT_RETRIES) {
            initRetryCount += 1;
            setTimeout(tryInit, 100);
          } else {
            this.init();
          }
        }
      };

      if (
        document.readyState === "complete" ||
        document.readyState === "interactive"
      ) {
        setTimeout(tryInit, 100);
      } else {
        const startCheck = () => {
          setTimeout(tryInit, 100);
        };
        window.addEventListener("load", startCheck, { once: true });
        document.addEventListener("DOMContentLoaded", startCheck, {
          once: true,
        });
      }
    } else {
      this.init();
    }
  }

  /**
   * 初始化 Bridge
   */
  private init() {
    if (typeof window === "undefined" || this.ready) {
      return;
    }

    const hasBridge =
      (window as any).bridge !== undefined && (window as any).bridge !== null;
    const hasHandleMessage =
      typeof (window as any).bridge?.handleMessage === "function";
    const hasWebKit = (window as any).webkit?.messageHandlers?.bridge;

    // 检测平台
    if (hasBridge && hasHandleMessage) {
      this.platform = "android";
    } else if (hasWebKit) {
      this.platform = "ios";
    } else {
      this.platform = "web";
    }

    // iOS: 检查是否有注入的设备信息（JS 注入方式，比消息传递更快）
    if (this.platform === "ios") {
      const injectedDeviceInfo = (window as any).__APP_DEVICE_INFO__;
      if (injectedDeviceInfo) {
        // 立即处理注入的设备信息
        this.handleInjectedDeviceInfo(injectedDeviceInfo);
      }

      // 监听设备信息就绪事件（备用方案）
      window.addEventListener("app-device-info-ready", ((
        event: CustomEvent,
      ) => {
        if (event.detail) {
          this.handleInjectedDeviceInfo(event.detail);
        }
      }) as EventListener);

      // 监听来自 App 的消息
      const handler = this.handleMessageFromApp.bind(this);
      window.addEventListener("app-bridge-message", handler);
    } else if (this.platform === "android") {
      // Android WebView 模式：通过 JavaScript Interface
      this.setupWebViewMessageListener();

      // 重要：Android 端通过 window.postMessage 发送响应消息
      // 需要监听 message 事件来接收响应
      window.addEventListener("message", this.handleMessageFromApp.bind(this));

      // 检查是否有注入的设备信息（类似 iOS）
      const injectedDeviceInfo = (window as any).__APP_DEVICE_INFO__;
      if (injectedDeviceInfo) {
        this.handleInjectedDeviceInfo(injectedDeviceInfo);
      }

      // 监听设备信息就绪事件
      window.addEventListener("app-device-info-ready", ((
        event: CustomEvent,
      ) => {
        if (event.detail) {
          this.handleInjectedDeviceInfo(event.detail);
        }
      }) as EventListener);
    }

    // 标记为就绪
    this.ready = true;

    // 发送缓存的消息
    this.flushMessageQueue();

    // 发送页面就绪消息
    this.sendPageReady();
  }

  /**
   * 设置 WebView 模式的消息监听器
   */
  private setupWebViewMessageListener() {
    // WebView 模式下，App 会通过 window.bridge.receiveMessage 发送消息
    // 我们需要设置 receiveMessage 方法来接收消息
    if (!(window as any).bridge) {
      (window as any).bridge = {};
    }

    // 设置 receiveMessage 方法（Android WebView 通过 JavaScript Interface 调用）
    (window as any).bridge.receiveMessage = (message: any) => {
      // 处理来自 App 的消息
      if (typeof message === "string") {
        try {
          const parsed = JSON.parse(message);
          this.handleMessageFromApp({
            data: parsed,
          } as MessageEvent);
        } catch (e) {
          Track.log(
            "error",
            "bridge",
            "Bridge: Failed to parse message from App",
            { cause: String(e) },
          );
        }
      } else if (message && typeof message === "object") {
        // 直接是对象
        this.handleMessageFromApp({
          data: message,
        } as MessageEvent);
      }
    };

    // 同时监听 window.postMessage（Android 通过 evaluateJavascript 发送）
    window.addEventListener("message", (event: MessageEvent) => {
      // 只处理来自同源的消息（Android WebView 发送的）
      if (event.origin === window.location.origin || event.origin === "*") {
        try {
          const message =
            typeof event.data === "string"
              ? JSON.parse(event.data)
              : event.data;

          // 如果是 Bridge 消息（有 type 字段），处理它
          if (message && message.type) {
            this.handleMessageFromApp({
              data: message,
            } as MessageEvent);
          }
        } catch (e) {
          // 忽略解析错误（可能是其他消息）
        }
      }
    });

    // 也检查是否有注入的设备信息（类似 iOS）
    const injectedDeviceInfo = (window as any).__APP_DEVICE_INFO__;
    if (injectedDeviceInfo) {
      this.handleInjectedDeviceInfo(injectedDeviceInfo);
    } else {
    }

    // 监听设备信息就绪事件
    window.addEventListener("app-device-info-ready", ((event: CustomEvent) => {
      if (event.detail) {
        this.handleInjectedDeviceInfo(event.detail);
      }
    }) as EventListener);
  }

  /**
   * 处理注入的设备信息（iOS JS 注入方式，WebView 也支持）
   */
  /**
   * 处理注入的设备信息（iOS JS 注入方式，WebView 也支持）
   */
  private handleInjectedDeviceInfo(deviceInfo: any) {
    if (!deviceInfo) {
      Track.log("warn", "bridge", "Bridge: ⚠️ deviceInfo is null or undefined");
      return;
    }

    // 如果 deviceInfo 已经是完整的 app-init 格式（包含 platform, appVersion 等）
    if (deviceInfo.platform && deviceInfo.deviceInfo) {
      // 只处理一次 app-init
      if (this.appInitProcessed) {
        return;
      }
      this.appInitProcessed = true;

      // 触发 app-init 消息监听器
      const appInitListeners = this.messageListeners.get(MessageType.APP_INIT);
      if (appInitListeners && appInitListeners.size > 0) {
        appInitListeners.forEach((listener) => {
          try {
            listener(deviceInfo);
          } catch (e) {
            Track.log("error", "bridge", "Bridge: Error in app-init listener", {
              cause: String(e),
            });
          }
        });
      } else {
        Track.log(
          "warn",
          "bridge",
          "Bridge: ⚠️ No app-init listeners registered yet",
        );
      }

      // 也触发 device-info 消息监听器
      if (deviceInfo.deviceInfo) {
        const deviceInfoListeners = this.messageListeners.get(
          MessageType.DEVICE_INFO,
        );
        if (deviceInfoListeners && deviceInfoListeners.size > 0) {
          const info = deviceInfo.deviceInfo;
          deviceInfoListeners.forEach((listener) => {
            try {
              listener(info);
            } catch (e) {
              Track.log(
                "error",
                "bridge",
                "Bridge: Error in device-info listener",
                { cause: String(e) },
              );
            }
          });
        }
      }
    } else {
      // 只有设备信息部分，触发 device-info 监听器
      const deviceInfoListeners = this.messageListeners.get(
        MessageType.DEVICE_INFO,
      );
      if (deviceInfoListeners && deviceInfoListeners.size > 0) {
        deviceInfoListeners.forEach((listener) => {
          try {
            listener(deviceInfo);
          } catch (e) {
            Track.log(
              "error",
              "bridge",
              "Bridge: Error in device-info listener",
              { cause: String(e) },
            );
          }
        });
      }
    }
  }

  /**
   * 处理来自 App 的消息
   */
  private handleMessageFromApp(event: Event | MessageEvent) {
    let message: any;

    if (event instanceof MessageEvent) {
      // 忽略非 Bridge 消息：Next.js/Turbopack 等运行时会用 window.postMessage 发 "!_" + JSON，不是 App 桥接协议
      const raw = event.data;
      if (typeof raw === "string" && raw.startsWith("!_")) {
        return;
      }
      // Android Custom Tab PostMessage 或 WebView PostMessage（event.data 才是实际 payload）
      try {
        message = typeof raw === "string" ? JSON.parse(raw) : raw;
      } catch (e) {
        Track.error(e, {
          tag: "Bridge",
          msg: "Failed to parse message from App",
        });
        return;
      }
    } else if (event instanceof CustomEvent) {
      // iOS CustomEvent
      message = event.detail;
    } else {
      return;
    }

    // 处理响应消息
    // 响应消息格式：{ id: "...", success: true, data: {...}, error?: { code, errMsg } | string（兼容旧版） }
    // 判断标准：如果有 id 且有 success 字段（或 error 字段），则认为是响应消息
    if (
      message.id &&
      (message.success !== undefined || message.error !== undefined)
    ) {
      if (this.responseHandlers.has(message.id)) {
        const handler = this.responseHandlers.get(message.id)!;
        handler({
          id: message.id,
          success: message.success !== undefined ? message.success : true,
          data: message.data,
          error: message.error,
        });
        this.responseHandlers.delete(message.id);
        return;
      }
    }

    // 处理普通消息（有 type 字段）
    if (message.type) {
      logPaymentTraceFromApp(message);

      // 如果是 app-init 消息，确保只处理一次，并同步到 window.__APP_DEVICE_INFO__
      // 这样在部分机型（如部分 Samsung）上 evaluateJavascript 注入失败时，
      // 依赖该变量的代码（如 global.js 的 app_info/device_info 日志）仍能拿到数据
      if (message.type === MessageType.APP_INIT) {
        if (this.appInitProcessed) {
          return;
        }
        this.appInitProcessed = true;
        if (message.data && typeof window !== "undefined") {
          try {
            const prev = (window as any).__APP_DEVICE_INFO__;
            if (!prev || typeof prev !== "object") {
              (window as any).__APP_DEVICE_INFO__ = message.data;
            } else {
              const mergedDeviceInfo = {
                ...(prev.deviceInfo || {}),
                ...(message.data.deviceInfo || {}),
              };
              const mergedFeatures = {
                ...(prev.features || {}),
                ...(message.data.features || {}),
              };
              (window as any).__APP_DEVICE_INFO__ = {
                ...prev,
                ...message.data,
                deviceInfo: mergedDeviceInfo,
                features: mergedFeatures,
              };
            }
            window.dispatchEvent(
              new CustomEvent("app-device-info-ready", {
                detail: (window as any).__APP_DEVICE_INFO__,
              }),
            );
          } catch (e) {
            Track.log(
              "warn",
              "Bridge",
              "Sync app-init to __APP_DEVICE_INFO__ failed",
              { cause: String(e) },
            );
          }
        }
      }

      const listeners = this.messageListeners.get(message.type);
      if (listeners) {
        listeners.forEach((listener) => {
          try {
            listener(message.data);
          } catch (e) {
            Track.log(
              "error",
              "Bridge",
              `Error in listener for ${message.type}:`,
              { cause: String(e) },
            );
          }
        });
      }
    }
  }

  /**
   * 发送消息到 App
   */
  private sendToApp(message: BaseMessage): void {
    const messageJson = JSON.stringify(message);

    const platform = this.resolvePlatform();
    if (platform === "web") {
      return;
    }
    if (platform === "ios") {
      // ✅ 对于 iOS，即使 ready = false，只要 messageHandlers 存在，也直接发送
      // 因为 iOS 的 messageHandlers 在页面加载时就已经可用
      if ((window as any).webkit?.messageHandlers?.bridge) {
        try {
          (window as any).webkit.messageHandlers.bridge.postMessage(message);
          return; // ✅ iOS 直接发送成功，不需要加入队列
        } catch (error) {
          Track.log("error", "bridge", "Bridge: iOS postMessage failed:", {
            cause: String(error),
          });
          // 如果发送失败，继续下面的逻辑（加入队列）
        }
      } else {
        Track.log("warn", "bridge", "Bridge: iOS bridge handler not available");
      }
    } else if (platform === "android") {
      // Android WebView 模式：使用 JavaScript Interface
      const hasBridge = (window as any).bridge !== undefined;
      const hasHandleMessage =
        typeof (window as any).bridge?.handleMessage === "function";

      if (hasBridge && hasHandleMessage) {
        try {
          (window as any).bridge.handleMessage(messageJson);
          return; // ✅ Android 发送成功
        } catch (e) {
          Track.log("error", "bridge", "JavaScript Interface failed:", {
            cause: String(e),
          });
        }
      } else {
        Track.log("warn", "bridge", "Bridge: Android bridge not available");
      }
    }

    // ✅ 如果 ready = false 且消息没有被发送（iOS 发送失败或 Android 发送失败），将消息加入队列
    if (!this.ready) {
      Track.log("error", "bridge", "Bridge: ready = false，消息加入队列:", {
        cause: String(message),
      });
      this.messageQueue.push(message);
    }
  }

  /**
   * 懒检测：已确认为 App 时用缓存；仍为 web 时再探测一次并回写（覆盖 bridge 注入晚于单例构造的窗口）。
   */
  private resolvePlatform(): Platform {
    if (this.platform !== "web") {
      return this.platform;
    }
    const detected = detectPlatform();
    if (detected !== "web") {
      this.platform = detected;
    }
    return detected;
  }

  /**
   * 发送消息
   */
  send(type: string, data?: any): void {
    this.sendToApp({ type, data });
  }

  /**
   * 发送请求消息（带响应）
   */
  request(
    type: string,
    data?: any,
    options?: { timeoutMs?: number; id?: string },
  ): Promise<ResponseMessage> {
    return new Promise((resolve, reject) => {
      const id =
        typeof options?.id === "string" && options.id.length > 0
          ? options.id
          : this.generateId();

      this.responseHandlers.set(id, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          const errorMessage =
            getNativeErrorMessage(response.error) ??
            getNativeErrorMessage(response.data) ??
            "Request failed";
          const bre = parseBridgeErrorPayload(response.error);
          const err = new Error(errorMessage) as BridgeRequestError;
          if (bre) {
            err.bridgeError = bre;
          }
          reject(err);
        }
      });

      // 设置超时
      const timeoutMs =
        typeof options?.timeoutMs === "number" && options.timeoutMs > 0
          ? options.timeoutMs
          : 30000;
      setTimeout(() => {
        if (this.responseHandlers.has(id)) {
          this.responseHandlers.delete(id);
          recordBridgeTimeout(type, timeoutMs);
          reject(new Error("Request timeout"));
        }
      }, timeoutMs);

      this.sendToApp({ type, id, data });
    });
  }

  /**
   * 监听来自 App 的消息
   */
  on(type: string, callback: (data: any) => void): () => void {
    if (!this.messageListeners.has(type)) {
      this.messageListeners.set(type, new Set());
    }

    this.messageListeners.get(type)!.add(callback);

    // ✅ 兜底：如果 App 已经通过变量注入了 __APP_DEVICE_INFO__，但订阅发生得较晚（机型/Release 时序差异），
    // 这里主动回放一次，避免 Web 侧一直拿不到 features.nativePayment 从而错误降级到 Stripe。
    if (typeof window !== "undefined" && type === MessageType.APP_INIT) {
      try {
        const injected = (window as any).__APP_DEVICE_INFO__;
        if (injected && injected.platform && injected.deviceInfo) {
          // 异步回放，避免影响当前调用栈
          setTimeout(() => {
            try {
              callback(injected);
            } catch {
              // ignore
            }
          }, 0);
        }
      } catch {
        // ignore
      }
    }

    // 返回取消监听的函数
    return () => {
      const listeners = this.messageListeners.get(type);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }

  /**
   * 发送页面就绪消息
   * 注意：这是一个通知消息，不需要响应
   */
  private sendPageReady(): void {
    // page-ready 是通知消息，使用 send 而不是 request
    this.send(MessageType.PAGE_READY);
  }

  /**
   * 通知 App 首屏可交互，可隐藏启动屏（冷启动体验优化）
   * 应在登录态已判定且首屏关键数据（如会员信息）就绪后调用一次。
   */
  sendAppInteractiveReady(): void {
    this.send(MessageType.APP_INTERACTIVE_READY);
  }

  /**
   * 请求设备信息
   */
  requestDeviceInfo(): Promise<any> {
    return this.request(MessageType.REQUEST_DEVICE_INFO).then(
      (response) => response.data,
    );
  }

  /**
   * 请求推送 Token
   */
  requestPushToken(): Promise<string> {
    return this.request(MessageType.REQUEST_PUSH_TOKEN).then(
      (response) => response.data,
    );
  }

  /**
   * 请求 GA4 gaInstanceId / sessionId（弥补 __APP_DEVICE_INFO__ 注入失败或延迟）
   * 仅在 App 内有效；旧壳未实现会超时，调用方需做超时降级。
   */
  requestGAInfo(): Promise<{ gaInstanceId?: string; sessionId?: string }> {
    return this.request(MessageType.REQUEST_GA_INFO, undefined, {
      timeoutMs: 8000,
    }).then(
      (response) =>
        (response.data ?? {}) as { gaInstanceId?: string; sessionId?: string },
    );
  }

  /**
   * App 内：经 Bridge 由原生 Adjust SDK 上报事件。
   * 非 App 环境为 no-op。
   */
  adjustTrackEvent(
    payload: AdjustTrackEventPayload,
    options?: { timeoutMs?: number },
  ): Promise<void> {
    if (!this.isInApp()) {
      return Promise.resolve();
    }
    const nextPayload = (() => {
      // 调用方仅传 eventKey 时，在 Web 侧先映射成最终 eventToken 再下发原生。
      if (payload.eventToken || !payload.eventKey) {
        return payload;
      }

      const { eventKey, eventToken: _eventToken, ...restPayload } = payload;
      const mappedPayload = buildAdjustPayloadFromEventKey(
        eventKey,
        restPayload,
      );
      if (!mappedPayload) {
        Track.log(
          "info",
          "bridge",
          "adjustTrackEvent skipped: unresolved event key",
          {
            eventKey,
          },
        );
        return null;
      }
      return mappedPayload;
    })();

    if (!nextPayload) {
      return Promise.resolve();
    }

    return this.request(MessageType.ADJUST_TRACK_EVENT, nextPayload, {
      timeoutMs: options?.timeoutMs ?? 15000,
    }).then(() => undefined);
  }

  /**
   * App 内：上报订阅信息（iOS App Store / Android Play Store）
   * 非 App 环境为 no-op。
   */
  adjustTrackSubscription(
    payload: AdjustTrackSubscriptionPayload,
    options?: { timeoutMs?: number },
  ): Promise<void> {
    if (!this.isInApp()) {
      return Promise.resolve();
    }
    return this.request(MessageType.ADJUST_TRACK_SUBSCRIPTION, payload, {
      timeoutMs: options?.timeoutMs ?? 15000,
    }).then(() => undefined);
  }

  /**
   * App 内：更新 iOS SKAdNetwork 转化值（Android 自动 no-op）。
   * 非 App 环境为 no-op。
   */
  adjustUpdateSkanConversionValue(
    payload: AdjustUpdateSkanConversionValuePayload,
    options?: { timeoutMs?: number },
  ): Promise<void> {
    if (!this.isInApp() || this.platform !== "ios") {
      return Promise.resolve();
    }
    return this.request(
      MessageType.ADJUST_UPDATE_SKAN_CONVERSION_VALUE,
      payload,
      {
        timeoutMs: options?.timeoutMs ?? 15000,
      },
    ).then(() => undefined);
  }

  /** App 内：获取 Adjust ADID；非 App 返回 null */
  adjustGetAdid(options?: { timeoutMs?: number }): Promise<string | null> {
    if (!this.isInApp()) {
      return Promise.resolve(null);
    }
    return this.request(MessageType.ADJUST_GET_ADID, undefined, {
      timeoutMs: options?.timeoutMs ?? 15000,
    }).then((response) => {
      const d = response.data as { adid?: string } | undefined;
      const adid = d?.adid;
      return typeof adid === "string" && adid.length > 0 ? adid : null;
    });
  }

  /**
   * App 内（仅 Android）：Google Advertising ID（GAID）。iOS 无此消息，不应调用。
   * 与 Adjust ADID 不同；`nativeDeviceId` 在 GAID 失败时可能为 Firebase，本接口仅返回真实 GAID 或 null。
   */
  osGetAdvertisingId(options?: {
    timeoutMs?: number;
  }): Promise<OsGetAdvertisingIdData> {
    if (!this.isInApp() || this.getPlatform() !== "android") {
      return Promise.resolve({});
    }
    return this.request(MessageType.OS_GET_ADVERTISING_ID, undefined, {
      timeoutMs: options?.timeoutMs ?? 15000,
    }).then((response) => (response.data ?? {}) as OsGetAdvertisingIdData);
  }

  /**
   * App 内（仅 iOS）：请求 ATT 授权状态并回传 IDFA。
   * - iOS：由原生调用 Adjust ATT wrapper；未授权时 idfa 可能为空字符串。
   * - Android / Web：返回空对象。
   */
  requestAppTrackingTransparency(options?: {
    timeoutMs?: number;
  }): Promise<OsAppTrackingTransparencyData> {
    if (!this.isInApp() || this.getPlatform() !== "ios") {
      return Promise.resolve({});
    }
    return this.request(
      MessageType.REQUEST_APP_TRACKING_TRANSPARENCY,
      undefined,
      {
        timeoutMs: options?.timeoutMs ?? 20000,
      },
    ).then(
      (response) => (response.data ?? {}) as OsAppTrackingTransparencyData,
    );
  }

  /** App 内：读取原生缓存的 Adjust 归因；非 App 返回空对象 */
  adjustGetAttribution(options?: {
    timeoutMs?: number;
  }): Promise<AdjustAttributionData> {
    if (!this.isInApp()) {
      return Promise.resolve({});
    }
    return this.request(MessageType.ADJUST_GET_ATTRIBUTION, undefined, {
      timeoutMs: options?.timeoutMs ?? 15000,
    }).then((response) => (response.data ?? {}) as AdjustAttributionData);
  }

  /** App 内：拉取最近一次 deep-link-opened 解析结果（含 resultId）；无缓存时 reject */
  getLastDeepLink(options?: {
    timeoutMs?: number;
  }): Promise<DeepLinkOpenedData> {
    if (!this.isInApp()) {
      return Promise.reject(new Error("Not in App environment"));
    }
    return this.request(MessageType.GET_LAST_DEEP_LINK, undefined, {
      timeoutMs: options?.timeoutMs ?? 10000,
    }).then((response) => (response.data ?? {}) as DeepLinkOpenedData);
  }

  /** App 内：设置 Adjust external device id；非 App 为 no-op */
  adjustSetExternalDeviceId(
    externalDeviceId: string,
    options?: { timeoutMs?: number },
  ): Promise<void> {
    if (!this.isInApp()) {
      return Promise.resolve();
    }
    const id = externalDeviceId.trim();
    if (!id) {
      return Promise.resolve();
    }
    return this.request(
      MessageType.ADJUST_SET_EXTERNAL_DEVICE_ID,
      { externalDeviceId: id },
      { timeoutMs: options?.timeoutMs ?? 15000 },
    ).then(() => undefined);
  }

  /**
   * 获取原生默认偏好（string）
   * - 标准协议：response.data 为 `{ value: string | null }`，本方法会解包并返回 `string | null`
   * - 兼容旧实现：response.data 直接为 string | null
   * - Web 非 App 环境下直接降级为 null（避免 request 超时）
   */
  getDefaultPreferenceString(
    key: string,
    options?: { timeoutMs?: number },
  ): Promise<string | null> {
    if (!this.isInApp()) {
      return Promise.resolve(null);
    }

    const timeoutMs =
      typeof options?.timeoutMs === "number" && options.timeoutMs > 0
        ? options.timeoutMs
        : undefined;

    const requestOptions = timeoutMs !== undefined ? { timeoutMs } : undefined;

    return this.request(
      BRIDGE_MSG_DEFAULT_PREF,
      { action: "get", key },
      requestOptions,
    ).then((response) => {
      const data = response.data as unknown;

      // 兼容原生直接返回 string | null
      if (typeof data === "string") {
        return data;
      }
      if (data === null || data === undefined) {
        return null;
      }

      // 标准形态：{ value: string | null }
      if (typeof data === "object" && "value" in (data as object)) {
        const v = (data as { value?: unknown }).value;
        if (v === null || v === undefined) {
          return null;
        }
        return String(v);
      }

      return null;
    });
  }

  /**
   * 设置原生默认偏好（string）
   * - Web 非 App 环境下直接 no-op（避免 request 超时）
   */
  setDefaultPreferenceString(
    key: string,
    value: string,
    options?: { timeoutMs?: number },
  ): Promise<void> {
    if (!this.isInApp()) {
      return Promise.resolve();
    }

    const timeoutMs =
      typeof options?.timeoutMs === "number" && options.timeoutMs > 0
        ? options.timeoutMs
        : undefined;
    const requestOptions = timeoutMs !== undefined ? { timeoutMs } : undefined;

    return this.request(
      BRIDGE_MSG_DEFAULT_PREF,
      { action: "set", key, value },
      requestOptions,
    ).then(() => undefined);
  }

  /**
   * 清除原生默认偏好（清空全部 key）
   * - Web 非 App 环境下直接 no-op（避免 request 超时）
   */
  clearAllDefaultPreferenceStrings(options?: {
    timeoutMs?: number;
  }): Promise<void> {
    if (!this.isInApp()) {
      return Promise.resolve();
    }

    const timeoutMs =
      typeof options?.timeoutMs === "number" && options.timeoutMs > 0
        ? options.timeoutMs
        : undefined;
    const requestOptions = timeoutMs !== undefined ? { timeoutMs } : undefined;

    return this.request(
      BRIDGE_MSG_DEFAULT_PREF,
      { action: "clearAll" },
      requestOptions,
    ).then(() => undefined);
  }

  /**
   * 删除原生默认偏好
   * 说明：delete 与 clear 等价：按 key 删除。
   */
  deleteDefaultPreferenceString(
    key: string,
    options?: { timeoutMs?: number },
  ): Promise<void> {
    if (!this.isInApp()) {
      return Promise.resolve();
    }

    const timeoutMs =
      typeof options?.timeoutMs === "number" && options.timeoutMs > 0
        ? options.timeoutMs
        : undefined;
    const requestOptions = timeoutMs !== undefined ? { timeoutMs } : undefined;

    return this.request(
      BRIDGE_MSG_DEFAULT_PREF,
      { action: "delete", key },
      requestOptions,
    ).then(() => undefined);
  }

  /**
   * 调用 App 方法
   */
  callMethod(method: string, params?: any): Promise<any> {
    return this.request(MessageType.CALL_METHOD, { method, params }).then(
      (response) => response.data,
    );
  }

  /**
   * 发送缓存的消息
   */
  private flushMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (message) {
        this.sendToApp(message);
      }
    }
  }

  /**
   * 生成唯一 ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Web → Native：`container-control`（与 iOS `BridgeManager` / Android `MSG_TYPE_CONTAINER_CONTROL` 对齐）。
   */
  private sendContainerControl(payload: Record<string, unknown>): void {
    if (!this.isInApp()) {
      return;
    }
    this.send(MessageType.CONTAINER_CONTROL, payload);
  }

  /**
   * Web → Native：`container-broadcast`（原生中继到其它 WebView，见 `useContainerBroadcastStateSync`）。
   */
  broadcastContainerEvent(
    event: ContainerBroadcastEvent | string,
    payload?: Record<string, unknown>,
  ): void {
    if (!this.isInApp()) {
      return;
    }
    const ctx = getContainerRouteContext();
    const data: Record<string, unknown> = {
      event,
      ts: Date.now(),
      ...payload,
    };
    if (ctx.tabIndex != null && ctx.tabIndex >= 0) {
      data.sourceTabIndex = ctx.tabIndex;
    }
    if (ctx.containerId) {
      data.sourceContainerId = ctx.containerId;
    }
    this.send(MessageType.CONTAINER_BROADCAST, data);
  }

  /** 通知其它壳 Tab / Detail WebView 刷新会员相关接口 */
  broadcastMemberUpdated(payload?: Record<string, unknown>): void {
    this.broadcastContainerEvent(
      ContainerBroadcastEvent.MEMBER_UPDATED,
      payload,
    );
  }

  /**
   * 通知其它 WebView 从共享 localStorage 恢复登录态（邮箱登录等多 WebView 同步）。
   * 不在 payload 中传递 token，接收方只读本地缓存。
   */
  broadcastAuthSessionUpdated(payload?: {
    loginType?: number;
    userId?: string;
  }): void {
    this.broadcastContainerEvent(
      ContainerBroadcastEvent.AUTH_SESSION_UPDATED,
      payload,
    );
  }

  /**
   * 通知其它 WebView：指定全局 modal 已由当前实例展示，避免多 Tab 叠弹。
   */
  broadcastGlobalAppModalClaimed(payload: { modalKey: string }): void {
    this.broadcastContainerEvent(
      ContainerBroadcastEvent.GLOBAL_APP_MODAL_CLAIMED,
      payload,
    );
  }

  /** App 内：打开原生二级 Detail 容器并加载指定 URL */
  openDetailInNative(url: string, options?: OpenDetailInNativeOptions): void {
    if (!url) {
      return;
    }
    const payload: Record<string, unknown> = {
      action: ContainerControlAction.OPEN_DETAIL,
      url,
    };
    if (options?.gestureBackEnabled === false) {
      payload.gestureBackEnabled = false;
    }
    this.sendContainerControl(payload);
  }

  /**
   * App 内：Detail 已打开时同步是否允许手势 / 系统返回关闭容器。
   * `enabled: false` 时仅允许 Web SPA history back，禁止 pop Detail。
   */
  setDetailGestureBack(enabled: boolean): void {
    this.sendContainerControl({
      action: ContainerControlAction.SET_DETAIL_GESTURE_BACK,
      enabled,
    });
  }

  /**
   * App 内：设置当前前台 WebView 对应原生容器的状态栏样式。
   * `light` = 深色图标（浅色背景）；`dark` = 浅色图标（深色背景）；`auto` = 原生默认。
   */
  setStatusBarStyle(style: NativeStatusBarStyle): void {
    if (!this.isInApp()) {
      return;
    }
    this.send(MessageType.SET_STATUS_BAR_STYLE, { style });
  }

  /** App 内：关闭原生 Detail 二级容器 */
  closeDetailInNative(): void {
    this.sendContainerControl({ action: ContainerControlAction.CLOSE_DETAIL });
  }

  /**
   * App 内：先 pop Detail，再在对应 Tab 壳 WebView 加载 URL（与原生 `pop-detail-navigate-shell` 对齐）。
   */
  popDetailAndNavigateShell(url: string): void {
    if (!url) {
      return;
    }
    this.sendContainerControl({
      action: ContainerControlAction.POP_DETAIL_NAVIGATE_SHELL,
      url,
    });
  }

  /**
   * App 内：逻辑主 Tab 切换（0=Brief, 1=Invest, 2=Plan, 3=Desk）。
   * 统一主壳下由原生在主 WebView loadUrl；Web 侧优先走 ShellLink SPA。Chat 请用 {@link presentShellChat}。
   */
  switchShellTab(tabIndex: number): void {
    if (!this.isInApp()) {
      return;
    }
    if (
      typeof tabIndex !== "number" ||
      tabIndex < 0 ||
      tabIndex > 3 ||
      !Number.isFinite(tabIndex)
    ) {
      return;
    }
    this.send(MessageType.SWITCH_SHELL_TAB, { tabIndex });
  }

  /**
   * App 内：拉起原生 EdChat（`open-native-chat`）。
   * 调用方应先确认 `features.nativeChat === true`；本方法不查 flag。
   */
  openNativeChat(payload?: {
    question?: string;
    autoSubmit?: boolean;
    autoRenew?: boolean;
  }): void {
    if (!this.isInApp()) {
      return;
    }
    this.send(MessageType.OPEN_NATIVE_CHAT, {
      question: payload?.question ?? "",
      autoSubmit: !!payload?.autoSubmit,
      autoRenew: !!payload?.autoRenew,
    });
  }

  /** App 内：自底部弹出 Chat 常驻 WebView（modal，底层主 Tab 仍可见并带遮罩）。 */
  presentShellChat(payload?: Record<string, unknown>): void {
    if (!this.isInApp()) {
      return;
    }
    this.send(MessageType.PRESENT_SHELL_CHAT, payload ? { payload } : {});
  }

  /** App 内：通知原生当前 Chat WebView 是否处于 loading。 */
  notifyShellChatLoading(loading: boolean): void {
    if (!this.isInApp()) {
      return;
    }
    this.send(MessageType.SHELL_CHAT_LOADING, { loading: !!loading });
  }

  /** App 内：关闭 Chat modal，回到弹出前的主 Tab。 */
  dismissShellChat(): void {
    if (!this.isInApp()) {
      return;
    }
    this.send(MessageType.DISMISS_SHELL_CHAT, {});
  }

  /**
   * 多 Tab 壳：在某一 Tab 内完成登出清理后通知原生，将各 WebView 重新加载为各自 seed，
   * 避免其它 Tab 仍保留旧登录态（与 iOS/Android Bridge 协议一致）。
   * Android 多 WebView 的 localStorage 跨 Tab 同步由原生注入脚本发 {@code shell-web-storage-sync} 完成，无需 Web 手动调用。
   */
  syncShellTabsAfterLogout(): void {
    if (typeof window === "undefined" || !this.isInApp()) {
      return;
    }
    this.send(MessageType.SYNC_SHELL_TABS_AFTER_LOGOUT);
  }

  /**
   * 通知 Native 执行 Firebase signOut + GoogleSignIn.signOut。
   * 确保 native 侧不残留旧的 Firebase 登录态，避免下次第三方登录时复用错误身份。
   */
  nativeFirebaseLogout(): void {
    if (typeof window === "undefined" || !this.isInApp()) {
      return;
    }
    this.send("firebase-auth-state", { action: "logout" });
  }

  /**
   * 多 Tab 壳：切换语言并写入 `locale` cookie 后通知原生同步各 Tab。
   * - `shell-tab-N`：排除 N，由当前 Tab 自行 `location` 跳转；其它 Tab 原生 reload。
   * - `native-detail`：Detail 自行 `location` 跳转；**全部** Shell Tab（含来源 Tab）原生 reload。
   */
  syncShellTabsAfterLocaleChange(localeOverride?: string): void {
    if (typeof window === "undefined" || !this.isInApp()) {
      return;
    }
    const { tabIndex, containerId } = getContainerRouteContext();
    const locale =
      typeof localeOverride === "string" && localeOverride.trim().length > 0
        ? localeOverride.trim().toLowerCase()
        : readLocaleCookieForShellSync();
    const payload: Record<string, unknown> = {};
    if (locale) {
      payload.locale = locale;
    }
    if (containerId === NATIVE_DETAIL_CONTAINER_ID) {
      payload.initiator = "native-detail";
    } else if (tabIndex !== null && tabIndex >= 0 && tabIndex <= 3) {
      payload.initiator = `shell-tab-${tabIndex}`;
      payload.sourceTabIndex = tabIndex;
    }
    this.send(MessageType.SYNC_SHELL_TABS_AFTER_LOCALE_CHANGE, payload);
  }

  /**
   * 获取当前平台
   */
  getPlatform(): Platform {
    return this.resolvePlatform();
  }

  /**
   * 检查是否在 App 环境中
   */
  isInApp(): boolean {
    return this.resolvePlatform() !== "web";
  }
}

// 创建单例实例
let bridgeInstance: Bridge | null = null;

/**
 * 获取 Bridge 实例
 */
export function getBridge(): Bridge {
  if (!bridgeInstance) {
    bridgeInstance = new Bridge();
  }
  return bridgeInstance;
}
